import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { is_mobile_view } from '../utils/AppUtils';
import SalesProfileFormFields from './SalesProfileFormFields';
import '../App.css';

const STEP_PICK = 'pick';
const STEP_CREATE = 'create';
const STEP_CREATE_NAME = 'create_name';
const STEP_CREATE_OFFERING = 'create_offering';
const STEP_DIFFICULTY = 'difficulty';

const DIFFICULTY_LEVELS = [
	{ value: 'Beginner',     desc: 'Very patient prospect, forgiving of mistakes' },
	{ value: 'Novice',       desc: 'Mild resistance, relatively patient' },
	{ value: 'Intermediate', desc: 'Standard challenge, balanced objections' },
	{ value: 'Advanced',     desc: 'Demanding and skeptical, less patient' },
	{ value: 'Expert',       desc: 'Extremely tough - only the best get through' },
];


const OBJECTION_STRENGTHS = ['very mild', 'mild', 'moderate', 'strong', 'very strong'];

const capitalize_words = function(str) {
	return str.split(' ').map(function(w) {
		return w.charAt(0).toUpperCase() + w.slice(1);
	}).join(' ');
};

const render_loading = () => {
	return (
		<div className="sp-wizard-loading">
			<Spinner animation="border" />
		</div>
	);
};

const render_error_alert = (error) => {
	if(!error) {
		return null;
	}
	return (
		<Alert variant="danger" className="sp-wizard-alert">
			{error}
		</Alert>
	);
};

const render_step_dots = (step) => {
	const steps = [STEP_CREATE_NAME, STEP_CREATE_OFFERING];
	const active_index = steps.indexOf(step);

	return (
		<div className="sp-wizard-step-dots">
			{steps.map((s, index) => {
				const dot_class = 'sp-wizard-step-dot' + (index <= active_index ? ' sp-wizard-step-dot-active' : '');
				return <div key={s} className={dot_class} />;
			})}
		</div>
	);
};

const render_profile_card = (profile, on_select) => {
	return (
		<div
			key={profile.id}
			className="sp-wizard-profile-card"
			onClick={() => { on_select(profile); }}>
			<div className="sp-wizard-profile-name">{profile.name}</div>
			<div className="sp-wizard-profile-category">{profile.solution_category}</div>
		</div>
	);
};

const render_pick_screen = (profiles, loading, on_select, on_create_new) => {
	return (
		<React.Fragment>
			<Modal.Header closeButton>
				<Modal.Title>Select a Sales Profile</Modal.Title>
			</Modal.Header>
			<Modal.Body className="sp-wizard-body">
				{loading ? render_loading() : (
					<React.Fragment>
						<p className="sp-wizard-intro">Choose an existing profile or create a new one to use for this session.</p>
						<div className="sp-wizard-profiles-grid">
							{profiles.map(profile => render_profile_card(profile, on_select))}
						</div>
					</React.Fragment>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="primary" onClick={on_create_new}>
					Create Sales Profile
				</Button>
			</Modal.Footer>
		</React.Fragment>
	);
};

const render_create_screen = (form, on_change, has_existing_profiles, saving, error, on_back, on_save) => {
	const can_save = form.name.trim().length > 0
		&& form.solution_category.trim().length > 0
		&& form.offering_description.trim().length > 0;

	return (
		<React.Fragment>
			<Modal.Header closeButton>
				<Modal.Title>Create Sales Profile</Modal.Title>
			</Modal.Header>
			<Modal.Body className="sp-wizard-body">
				{render_error_alert(error)}
				<div className="sp-wizard-step-content">
					<SalesProfileFormFields form={form} on_change={on_change} />
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={on_back} disabled={saving}>
					{has_existing_profiles ? 'Back' : 'Cancel'}
				</Button>
				<Button variant="primary" onClick={on_save} disabled={!can_save || saving}>
					{saving ? <Spinner animation="border" size="sm" /> : 'Start Training'}
				</Button>
			</Modal.Footer>
		</React.Fragment>
	);
};

const render_mobile_name_screen = (form, on_change, has_existing_profiles, on_back, on_next) => {
	const can_advance = form.name.trim().length > 0 && form.solution_category.trim().length > 0;

	return (
		<React.Fragment>
			<Modal.Header closeButton>
				<Modal.Title>Create Sales Profile</Modal.Title>
			</Modal.Header>
			<Modal.Body className="sp-wizard-body">
				{render_step_dots(STEP_CREATE_NAME)}
				<div className="sp-wizard-step-content">
					<div className="sp-wizard-field">
						<Form.Label className="sp-wizard-label">Profile Name</Form.Label>
						<p className="sp-wizard-hint">A short name so you can identify and re-use this profile later.</p>
						<Form.Control
							type="text"
							className="sp-wizard-input"
							placeholder="e.g. My CRM Software"
							value={form.name}
							onChange={e => { on_change('name', e.target.value); }}
						/>
					</div>
					<div className="sp-wizard-field">
						<Form.Label className="sp-wizard-label">Solution Category</Form.Label>
						<p className="sp-wizard-hint">The general category of what you are selling. Keep it broad.</p>
						<Form.Control
							type="text"
							className="sp-wizard-input"
							placeholder="e.g. CRM software, IT services, marketing platform"
							value={form.solution_category}
							onChange={e => { on_change('solution_category', e.target.value); }}
						/>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={on_back}>
					{has_existing_profiles ? 'Back' : 'Cancel'}
				</Button>
				<Button variant="primary" onClick={on_next} disabled={!can_advance}>
					Next
				</Button>
			</Modal.Footer>
		</React.Fragment>
	);
};

const render_mobile_offering_screen = (form, on_change, saving, error, on_back, on_save) => {
	const can_save = form.offering_description.trim().length > 0;

	return (
		<React.Fragment>
			<Modal.Header closeButton>
				<Modal.Title>Create Sales Profile</Modal.Title>
			</Modal.Header>
			<Modal.Body className="sp-wizard-body">
				{render_step_dots(STEP_CREATE_OFFERING)}
				{render_error_alert(error)}
				<div className="sp-wizard-step-content">
					<div className="sp-wizard-field">
						<Form.Label className="sp-wizard-label">Offering Description</Form.Label>
						<p className="sp-wizard-hint">Describe your specific product or service in detail. The AI prospect will use this to respond to your pitch and ask relevant questions.</p>
						<Form.Control
							as="textarea"
							rows={8}
							className="sp-wizard-input"
							placeholder="e.g. A cloud-based CRM platform that helps small businesses track leads, manage follow-ups, and close deals faster - without needing a dedicated IT team."
							value={form.offering_description}
							onChange={e => { on_change('offering_description', e.target.value); }}
						/>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={on_back} disabled={saving}>
					Back
				</Button>
				<Button variant="primary" onClick={on_save} disabled={!can_save || saving}>
					{saving ? <Spinner animation="border" size="sm" /> : 'Start Training'}
				</Button>
			</Modal.Footer>
		</React.Fragment>
	);
};

const render_difficulty_screen = (difficulty, settings, on_change_difficulty, on_change_setting, on_back, on_confirm) => {
	return (
		<React.Fragment>
			<Modal.Header closeButton>
				<Modal.Title>Set Difficulty</Modal.Title>
			</Modal.Header>
			<Modal.Body className="sp-wizard-body">
				<p className="sp-wizard-intro">Select a level to pre-fill the settings below, then adjust as needed.</p>
				<div className="sp-wizard-difficulty-list">
					{DIFFICULTY_LEVELS.map(function(level) {
						const is_active = difficulty === level.value;
						const card_class = 'sp-wizard-difficulty-card' + (is_active ? ' sp-wizard-difficulty-card-active' : '');
						return (
							<div
								key={level.value}
								className={card_class}
								onClick={function() { on_change_difficulty(level.value); }}>
								<div className="sp-wizard-difficulty-label">{level.value}</div>
								<div className="sp-wizard-difficulty-desc">{level.desc}</div>
							</div>
						);
					})}
				</div>
				<div className="sp-wizard-difficulty-settings">
					<div className="sp-wizard-difficulty-setting-row">
						<label className="sp-wizard-difficulty-setting-label">Required Objection Responses</label>
						<div className="sp-wizard-stepper">
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() { on_change_setting('required_3a_successes', Math.max(1, settings.required_3a_successes - 1)); }}>
								-
							</Button>
							<span className="sp-wizard-stepper-value">{settings.required_3a_successes}</span>
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() { on_change_setting('required_3a_successes', Math.min(10, settings.required_3a_successes + 1)); }}>
								+
							</Button>
						</div>
					</div>
					<div className="sp-wizard-difficulty-setting-row">
						<label className="sp-wizard-difficulty-setting-label">Patience Multiplier</label>
						<div className="sp-wizard-stepper">
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() {
									var cur = parseFloat(settings.patience_multiplier);
									on_change_setting('patience_multiplier', Math.max(0.1, Math.round((cur - 0.1) * 10) / 10).toFixed(1));
								}}>
								-
							</Button>
							<span className="sp-wizard-stepper-value">{settings.patience_multiplier}</span>
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() {
									var cur = parseFloat(settings.patience_multiplier);
									on_change_setting('patience_multiplier', Math.min(5.0, Math.round((cur + 0.1) * 10) / 10).toFixed(1));
								}}>
								+
							</Button>
						</div>
					</div>
					<div className="sp-wizard-difficulty-setting-row">
						<label className="sp-wizard-difficulty-setting-label">Objection Strength</label>
						<div className="sp-wizard-strength-buttons">
							{OBJECTION_STRENGTHS.map(function(s) {
								const is_active = settings.objection_strength === s;
								const btn_class = 'sp-wizard-strength-btn' + (is_active ? ' sp-wizard-strength-btn-active' : '');
								return (
									<Button
										key={s}
										className={btn_class}
										onClick={function() { on_change_setting('objection_strength', s); }}>
										{capitalize_words(s)}
									</Button>
								);
							})}
						</div>
					</div>
					<div className="sp-wizard-difficulty-setting-row">
						<label className="sp-wizard-difficulty-setting-label">Min Exchanges</label>
						<div className="sp-wizard-stepper">
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() { on_change_setting('min_exchanges', Math.max(1, settings.min_exchanges - 1)); }}>
								-
							</Button>
							<span className="sp-wizard-stepper-value">{settings.min_exchanges}</span>
							<Button
								className="sp-wizard-stepper-btn"
								onClick={function() { on_change_setting('min_exchanges', Math.min(30, settings.min_exchanges + 1)); }}>
								+
							</Button>
						</div>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-secondary" onClick={on_back}>Back</Button>
				<Button variant="primary" onClick={on_confirm}>Start Training</Button>
			</Modal.Footer>
		</React.Fragment>
	);
};

function SalesProfileWizard({ show, on_hide, on_complete }) {
	const { token } = useAuth();
	const is_mobile = is_mobile_view();

	const [step, set_step] = useState(STEP_PICK);
	const [profiles, set_profiles] = useState([]);
	const [loading, set_loading] = useState(false);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');
	const [form, set_form] = useState({
		name: '',
		solution_category: '',
		offering_description: '',
	});
	const [selected_profile, set_selected_profile] = useState(null);
	const [difficulty, set_difficulty] = useState('Intermediate');
	const [difficulty_settings, set_difficulty_settings] = useState(null);
	const [settings, set_settings] = useState(null);

	const get_initial_create_step = () => {
		if(is_mobile) {
			return STEP_CREATE_NAME;
		}
		return STEP_CREATE;
	};

	const initialize = async () => {
		set_loading(true);
		set_error('');
		const headers = token ? { Authorization: 'Bearer ' + token } : {};

		const [profiles_result, difficulty_result] = await Promise.allSettled([
			axios.get(Constants.SERVER_URL + '/api/sales_profiles', { headers }),
			axios.get(Constants.SERVER_URL + '/api/call/difficulty_settings', { headers }),
		]);

		let loaded = [];
		if(profiles_result.status === 'fulfilled') {
			loaded = profiles_result.value.data.profiles || [];
		}
		set_profiles(loaded);

		if(difficulty_result.status === 'fulfilled') {
			const fetched_settings = difficulty_result.value.data.difficulty_settings || {};
			set_difficulty_settings(fetched_settings);
			set_settings(fetched_settings['Intermediate'] || null);
		}

		if(loaded.length === 0) {
			set_step(get_initial_create_step());
		} else {
			set_step(STEP_PICK);
		}

		set_loading(false);
	};

	useEffect(() => {
		if(!show) {
			return;
		}
		set_form({ name: '', solution_category: '', offering_description: '' });
		set_selected_profile(null);
		set_difficulty('Intermediate');
		set_difficulty_settings(null);
		set_settings(null);
		initialize();
	}, [show]);

	const handle_change = (field, value) => {
		set_form(prev => {
			return Object.assign({}, prev, { [field]: value });
		});
	};

	const handle_select = (profile) => {
		set_selected_profile(profile);
		set_step(STEP_DIFFICULTY);
	};

	const handle_create_new = () => {
		set_step(get_initial_create_step());
	};

	const handle_back_from_create = () => {
		if(profiles.length > 0) {
			set_step(STEP_PICK);
		} else {
			on_hide();
		}
	};

	const handle_go_to_offering = () => {
		set_step(STEP_CREATE_OFFERING);
	};

	const handle_go_to_name = () => {
		set_step(STEP_CREATE_NAME);
	};

	const handle_save = async () => {
		set_saving(true);
		set_error('');
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const payload = Object.assign({}, form);
			const response = await axios.post(Constants.SERVER_URL + '/api/sales_profiles', payload, { headers });
			set_selected_profile(response.data.profile);
			set_step(STEP_DIFFICULTY);
		} catch (error) {
			set_error('Failed to save sales profile. Please try again.');
		} finally {
			set_saving(false);
		}
	};

	const handle_back_from_difficulty = () => {
		if(profiles.some(function(p) { return p.id === (selected_profile && selected_profile.id); })) {
			set_step(STEP_PICK);
		} else {
			set_step(get_initial_create_step());
		}
	};

	const handle_change_difficulty = (level) => {
		set_difficulty(level);
		set_settings(difficulty_settings ? difficulty_settings[level] : null);
	};

	const handle_change_setting = (key, value) => {
		set_settings(function(prev) {
			return Object.assign({}, prev, { [key]: value });
		});
	};

	const handle_confirm_difficulty = () => {
		on_complete(selected_profile, difficulty, settings);
	};

	const render_content = () => {
		if(step === STEP_PICK) {
			return render_pick_screen(profiles, loading, handle_select, handle_create_new);
		}
		if(step === STEP_CREATE) {
			return render_create_screen(form, handle_change, profiles.length > 0, saving, error, handle_back_from_create, handle_save);
		}
		if(step === STEP_CREATE_NAME) {
			return render_mobile_name_screen(form, handle_change, profiles.length > 0, handle_back_from_create, handle_go_to_offering);
		}
		if(step === STEP_CREATE_OFFERING) {
			return render_mobile_offering_screen(form, handle_change, saving, error, handle_go_to_name, handle_save);
		}
		if(step === STEP_DIFFICULTY) {
			if(!settings || !difficulty_settings) {
				return render_loading();
			}
			return render_difficulty_screen(difficulty, settings, handle_change_difficulty, handle_change_setting, handle_back_from_difficulty, handle_confirm_difficulty);
		}
		return null;
	};

	return (
		<Modal
			show={show}
			onHide={on_hide}
			centered
			dialogClassName="sp-wizard-dialog"
			backdrop="static">
			{render_content()}
		</Modal>
	);
}

export default SalesProfileWizard;
